import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Info } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';
import { PotTermHint } from './PotTermHint';

function TreasuryDonut({ reservedPct }) {
  const data = [
    { id: 'reserved', value: reservedPct, color: '#ffffff' },
    { id: 'free', value: Math.max(0, 100 - reservedPct), color: 'rgba(255,255,255,0.35)' },
  ].filter((row) => row.value > 0);

  if (!data.length) {
    return (
      <div className="relative mx-auto flex h-[7.25rem] w-[7.25rem] items-center justify-center">
        <div className="text-center">
          <p className="font-display text-2xl font-bold text-white">0%</p>
          <p className="text-[9px] uppercase tracking-wide text-white/85">
            {pt.pots.treasury.reservedLabel}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto h-[7.25rem] w-[7.25rem] shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart accessibilityLayer={false}>
          <Pie
            data={data}
            dataKey="value"
            cx="50%"
            cy="50%"
            innerRadius="62%"
            outerRadius="88%"
            startAngle={90}
            endAngle={-270}
            stroke="transparent"
            paddingAngle={data.length > 1 ? 1 : 0}
          >
            {data.map((entry) => (
              <Cell key={entry.id} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-display text-[1.45rem] font-bold leading-none text-white tnum">
          {reservedPct}%
        </p>
        <p className="mt-0.5 text-[9px] uppercase tracking-wide text-white/85">
          {pt.pots.treasury.reservedLabel}
        </p>
      </div>
    </div>
  );
}

/**
 * @param {{ overview: ReturnType<import('../../lib/potTreasuryUtils').computeTreasuryOverview> }} props
 */
export function PotTreasuryCard({ overview }) {
  const { reservedEur, freeEur, reservedPct, hasLiquidBaseline } = overview;

  return (
    <section className="rounded-[1.375rem] bg-sage-600 p-5 text-white shadow-sm">
      <div className="flex items-center gap-4">
        <TreasuryDonut reservedPct={reservedPct} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.07em] text-sage-100">
            {pt.pots.treasury.title}
            <PotTermHint term="reservedVsFree" variant="icon" tone="onSage" />
          </p>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded bg-white" aria-hidden />
              <span className="text-xs text-sage-100">{pt.pots.treasury.reserved}</span>
              <span className="ml-auto font-display text-base font-bold tabular-nums">
                {formatEUR(reservedEur)}
              </span>
            </div>
            {hasLiquidBaseline ? (
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded bg-white/35"
                  aria-hidden
                />
                <span className="text-xs text-sage-100">{pt.pots.treasury.free}</span>
                <span className="ml-auto font-display text-base font-bold tabular-nums">
                  {formatEUR(freeEur)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {!hasLiquidBaseline ? (
        <p className="mt-4 text-xs leading-relaxed text-sage-100">
          {pt.pots.treasury.noAssetsHint}{' '}
          <Link
            to={paths.ativos}
            className="font-semibold text-white underline underline-offset-2"
          >
            {pt.pots.treasury.noAssetsCta}
          </Link>
        </p>
      ) : null}
    </section>
  );
}

export function PotTreasuryHint({ overview }) {
  if (!overview.hasLiquidBaseline) return null;

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-[0.8125rem] border border-sage-100',
        'bg-sage-50 px-3.5 py-3 text-[11.5px] leading-relaxed text-sage-700',
        'dark:border-sage-900/50 dark:bg-sage-950/30 dark:text-sage-200',
      )}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p>
        <strong>{formatEUR(overview.reservedEur)}</strong>
        {' já têm dono (objectivos, ocasionais, emergência…). Só os '}
        <strong>{formatEUR(overview.freeEur)}</strong>
        {' livres podes gastar sem mexer nos planos. Cada cofre gere-se na sua página.'}
      </p>
    </div>
  );
}
